import type { DialNumberHistoryEntry, DialNumberLimit } from './ChangeDialNumberPopup';
import type { CallOutcomeExtra, CallOutcomeOption } from './Softphone';
import type { SoftphoneCarrierFailure, SoftphoneStatus } from './softphoneClient.types';
import { ESC_PRIORITY, useEscStack } from '../contexts/EscCancelable.context';
import { FloatingWidget } from '../FloatingWidget/FloatingWidget';
import { ChangeDialNumberPopup } from './ChangeDialNumberPopup';
import { Softphone } from './Softphone';

export interface SoftphoneDialerPureProps {
  /** Contact display info for the dialer card. */
  contactName?: string;
  phoneNumber: string;
  imageSrc?: string | null;
  /** The dialed number is the contact's org ("firm") number — surfaces a "Firm number" badge. */
  isFirmNumber?: boolean;

  // Softphone passthrough (the per-surface provider owns this state via useSoftphone).
  status: SoftphoneStatus;
  durationSec: number;
  isMuted: boolean;
  error: string | null;
  /** The carrier refused the just-ended call — see `SoftphoneProps.carrierFailure`. */
  carrierFailure?: SoftphoneCarrierFailure | null;
  isRecording?: boolean;
  /** The carrier's own dialer runs the call (JustCall) — see `SoftphoneProps.embeddedCarrierDialer`. */
  embeddedCarrierDialer?: boolean;
  /** A short instruction while the call is set up — see `SoftphoneProps.notice`. */
  notice?: string | null;
  outcomes: ReadonlyArray<CallOutcomeOption>;
  onToggleMute: () => void;
  onHangup: () => void;
  /** Send a DTMF digit on the live call. Undefined = keypad hidden. */
  onSendDigits?: (digits: string) => void;
  onSelectOutcome: (value: string, extra?: CallOutcomeExtra) => void;
  /** Close the dialer (ends any live call — the providers' close semantics). */
  onClose: () => void;

  // Sticky dialing-number passthrough (the provider owns the dial-info query + mutation).
  /** The contact's pinned from-number (or the in-flight call's number). */
  callerNumber?: string | null;
  /** The pinned number is no longer in the rep's bucket — warn; this call still uses it. */
  callerNumberStale?: boolean;
  /**
   * The rep's assigned numbers. The Change affordance shows when an alternative exists — or when
   * anything is pinned at all, since the popup is also the only way to unset a settled pin.
   */
  bucketNumbers?: string[];
  /** The contact's pin is settled and no longer rotates. */
  callerNumberLocked?: boolean;
  /** Which numbers this contact has actually been called from, most recently used first. */
  callerNumberHistory?: DialNumberHistoryEntry[];
  /** Each number's calls today against its daily limit, shown in the change-number picker. */
  callerNumberLimits?: DialNumberLimit[];
  changeNumberOpen?: boolean;
  changeNumberSaving?: boolean;
  changeNumberClearing?: boolean;
  /** Open the guarded change-number confirmation. Undefined = affordance hidden entirely. */
  onOpenChangeNumber?: () => void;
  onCloseChangeNumber?: () => void;
  onConfirmChangeNumber?: (phoneNumber: string) => void;
  /** Drop the association so calls rotate again. Undefined = the affordance is hidden. */
  onClearChangeNumber?: () => void;
}

/**
 * Pure, presentational floating dialer — the draggable `FloatingWidget` card the per-surface
 * `SoftphoneProvider` mounts ONCE, above the app, so a live call survives page/tab navigation.
 * No tRPC, no softphone client: the provider owns the network + WebRTC and feeds this its
 * controller state. Keeping the view pure lets it be component-tested without the carrier SDKs.
 *
 * Rendering this component IS "the dialer is open" — the provider only mounts it while a call
 * session is active. The team/phone gates (and the "already on a call" guard) live upstream on
 * the `ClickToCallButtonPure` trigger, so this component never has to decide whether to show.
 */
export function SoftphoneDialerPure({
  contactName,
  phoneNumber,
  imageSrc,
  isFirmNumber,
  status,
  durationSec,
  isMuted,
  error,
  carrierFailure,
  isRecording,
  embeddedCarrierDialer,
  notice,
  outcomes,
  onToggleMute,
  onHangup,
  onSendDigits,
  onSelectOutcome,
  onClose,
  callerNumber,
  callerNumberStale,
  bucketNumbers,
  callerNumberLocked,
  callerNumberHistory,
  callerNumberLimits,
  changeNumberOpen,
  changeNumberSaving,
  changeNumberClearing,
  onOpenChangeNumber,
  onCloseChangeNumber,
  onConfirmChangeNumber,
  onClearChangeNumber,
}: SoftphoneDialerPureProps) {
  const inCall = status === 'connecting' || status === 'ringing' || status === 'active';

  // ESC dismisses an idle/ended/error dialer (e.g. the outcome screen) but must NEVER hang up a
  // live call — the old modal's backdrop/ESC-close silently dropped calls. LOW priority so any
  // real popup stacked above (ChangeDialNumberPopup registers at INTERSTITIAL) wins ESC first.
  useEscStack('SOFTPHONE_FLOAT', ESC_PRIORITY.LOW, inCall ? undefined : onClose);

  return (
    <>
      <FloatingWidget
        onClose={onClose}
        // JustCall's call lives in its own dialer — closing ours only stops following it.
        closeTooltip={
          inCall ? (embeddedCarrierDialer ? 'Close (the call continues in JustCall)' : 'End call and close') : 'Close'
        }
        data-test="softphone-widget">
        <Softphone
          contactName={contactName}
          phoneNumber={phoneNumber}
          isFirmNumber={isFirmNumber}
          status={status}
          durationSec={durationSec}
          isMuted={isMuted}
          error={error}
          carrierFailure={carrierFailure}
          isRecording={isRecording}
          embeddedCarrierDialer={embeddedCarrierDialer}
          notice={notice}
          imageSrc={imageSrc}
          callerNumber={callerNumber}
          callerNumberStale={callerNumberStale}
          // Offer the guarded Change flow when there's another number to pick — or when anything is
          // pinned, since unsetting a settled pin runs through the same popup.
          onChangeNumber={
            bucketNumbers?.some((n) => n !== callerNumber) || (callerNumber != null && onClearChangeNumber != null)
              ? onOpenChangeNumber
              : undefined
          }
          outcomes={outcomes}
          onToggleMute={onToggleMute}
          onHangup={onHangup}
          onSendDigits={onSendDigits}
          onSelectOutcome={onSelectOutcome}
          onClose={onClose}
          // FloatingWidget owns the card chrome (rounded corners, shadow); the handle strip
          // above the card supplies the top spacing.
          className="pt-2"
        />
      </FloatingWidget>

      {changeNumberOpen && onCloseChangeNumber && onConfirmChangeNumber && (
        <ChangeDialNumberPopup
          contactName={contactName}
          currentNumber={callerNumber ?? null}
          locked={callerNumberLocked}
          bucketNumbers={bucketNumbers ?? []}
          history={callerNumberHistory}
          numberLimits={callerNumberLimits}
          saving={changeNumberSaving}
          clearing={changeNumberClearing}
          onConfirm={onConfirmChangeNumber}
          // Nothing pinned = nothing to unset.
          onClear={callerNumber == null ? undefined : onClearChangeNumber}
          onClose={onCloseChangeNumber}
        />
      )}
    </>
  );
}
